/**
 * Subject palette mapping.
 *
 * Maps a Subject (by name, id, or slug) to a CSS class that drives the
 * gradient + tint variables used by SubjectGlyph, SubjectTile, and ExamCard.
 *
 * The actual CSS lives in index.css under the `.s-*` selectors.
 *
 * paletteFor() call forms:
 *   paletteFor(null, title)          → fuzzy match on exam title string
 *   paletteFor({ id, name, slug })   → exact then fuzzy match on slug/name
 *   paletteFor("string")             → exact then fuzzy keyword match
 */

// ── Exact lookup table ────────────────────────────────────────────────────────
const SUBJECT_PALETTES = {
  // Uzbek subject names (lowercased)
  matematika:    's-math',
  fizika:        's-physics',
  ingliz:        's-english',
  'ingliz tili': 's-english',
  tarix:         's-history',
  biologiya:     's-biology',
  kimyo:         's-chemistry',
  informatika:   's-cs',
  // English / international aliases
  math:          's-math',
  mathematics:   's-math',
  algebra:       's-math',
  geometriya:    's-math',
  geometry:      's-math',
  physics:       's-physics',
  mexanika:      's-physics',
  mechanics:     's-physics',
  english:       's-english',
  history:       's-history',
  tarix:         's-history',
  biology:       's-biology',
  botanika:      's-biology',
  zoologiya:     's-biology',
  chemistry:     's-chemistry',
  cs:            's-cs',
  it:            's-cs',
}

// ── Fuzzy (substring) rules — order matters: most specific first ──────────────
const FUZZY_RULES = [
  { keys: ['fizika', 'physics', 'mexanika', 'elektr', 'magnit', 'optika'], palette: 's-physics' },
  { keys: ['ingliz', 'english'],                                            palette: 's-english' },
  { keys: ['biologiya', 'biology', 'botanika', 'zoologiya', 'bio'],        palette: 's-biology' },
  { keys: ['kimyo', 'chem'],                                                palette: 's-chemistry' },
  { keys: ['tarix', 'history'],                                             palette: 's-history' },
  { keys: ['informatika', 'dastur', 'prog', ' cs ', 'compút'],             palette: 's-cs' },
  { keys: ['matematik', 'algebra', 'geometr', 'math'],                     palette: 's-math' },
]

function fuzzyPalette(str) {
  const lower = str.toLowerCase()
  for (const { keys, palette } of FUZZY_RULES) {
    if (keys.some((k) => lower.includes(k))) return palette
  }
  return 's-math' // default
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Get the CSS palette class for a subject.
 *
 * @param {object|string|null} subject  — subject object, name string, or null
 * @param {string}             [title]  — exam title (used as fallback when subject is null)
 * @returns {string}  CSS class e.g. 's-biology'
 */
export function paletteFor(subject, title) {
  // null / undefined → try title, else default
  if (!subject) {
    return title ? fuzzyPalette(title) : 's-math'
  }

  // Plain string → exact lookup first, then fuzzy
  if (typeof subject === 'string') {
    const exact = SUBJECT_PALETTES[subject.toLowerCase().trim()]
    return exact ?? fuzzyPalette(subject)
  }

  // Object { slug?, name?, ... }
  const raw = (subject.slug ?? subject.name ?? '').toLowerCase().trim()
  const exact = SUBJECT_PALETTES[raw]
  return exact ?? fuzzyPalette(raw)
}

/**
 * Get the display glyph (1-3 characters) for a subject.
 * Used when the Subject has no logo image uploaded.
 */
const SUBJECT_GLYPHS = {
  's-math':      'π',
  's-physics':   'Φ',
  's-english':   'Aa',
  's-history':   '⌛',
  's-biology':   'β',
  's-chemistry': '⚗',
  's-cs':        '{ }',
}

export function glyphFor(subject, title) {
  return SUBJECT_GLYPHS[paletteFor(subject, title)] ?? 'A'
}

/**
 * Extract a human-readable subject name from a generated exam title.
 *
 * "[Seed] Biologiya — namuna imtihon"  →  "Biologiya"
 * "[Seed] Ingliz tili — namuna imtihon"  →  "Ingliz tili"
 * "Oddiy sarlavha"  →  "Oddiy sarlavha"  (passthrough)
 */
export function extractSubjectFromTitle(title) {
  if (!title) return ''
  // Strip leading bracketed prefix like "[Seed] ", "[Demo] " etc.
  let s = title.replace(/^\[[^\]]*\]\s*/u, '')
  // Take the part before " — " (em dash separator)
  const dashIdx = s.indexOf(' — ')
  if (dashIdx !== -1) s = s.slice(0, dashIdx)
  return s.trim()
}
